import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Form, Input, Modal, Popconfirm, Skeleton, Table, Tabs, Tag, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { FiArrowLeft, FiPlus, FiUpload, FiX } from 'react-icons/fi';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import PermissionGate from '../../components/auth/PermissionGate';
import BasicProfileEditDrawer from '../../components/profile/BasicProfileEditDrawer';
import BankMismatchReviewPanel from '../../components/bank/BankMismatchReviewPanel';
import BankManualUploadForm from '../../components/bank/BankManualUploadForm';
import BankRequestSubmittedModal from '../../components/bank/BankRequestSubmittedModal';
import { PERMISSIONS } from '../../config/permissions';
import { useAuth } from '../../contexts/AuthContext';
import ifaService from '../../services/ifaService';
import investorProfileService from '../../services/investorProfileService';
import PreIpoTransactionsTab from './_blocks/PreIpoTransactionsTab';
import {
  getInvestorPreIpoStatusColor,
  getInvestorPreIpoStatusLabel,
  INVESTOR_PROFILE_TAB_KEYS,
  canViewInvestorProfileTab,
  getAllowedInvestorProfileTabs,
  resolveInvestorProfileTabFromParams,
} from './commons/investorProfile.helpers';
import { getDematOcrUiState } from './commons/dematOcrVerification';
import {
  DEMAT_FORMAT_HINT,
  DEMAT_MAX_LENGTH,
  formatDematInput,
  getDematInputError,
  validateDematNumber,
} from '../../utils/dematValidation';
import {
  applyProfileBankManualUpload,
  buildBankManualUploadState,
  getProfileAttemptedBankFields,
  resetProfileBankAddForm,
} from '../../utils/bankVerifyManualUpload';

const TAB_KEYS = INVESTOR_PROFILE_TAB_KEYS;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const DEMAT_CMR_MAX_SIZE = 2 * 1024 * 1024;
const DEMAT_CMR_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const DEMAT_CMR_ALLOWED_EXTENSIONS = /\.(pdf|jpg|jpeg|png|webp)$/i;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY, hh:mm A') : '—');

const safeValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return value;
};

const normalizeDocumentUrl = (url) => {
  if (!url) return '';
  const duplicatedHostMatch = String(url).match(/^(https?:\/\/[^/]+)\/\1(\/.*)$/);
  if (duplicatedHostMatch) {
    return `${duplicatedHostMatch[1]}${duplicatedHostMatch[2]}`;
  }
  return String(url);
};

const InfoGrid = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map((item) => (
      <div key={item.label} className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{item.label}</p>
        <p className="text-sm text-gray-900 font-semibold mt-1 wrap-break-word">{safeValue(item.value)}</p>
      </div>
    ))}
  </div>
);

const PLACE_SINGLE_SHARE_ORDER_PATH = '/pre-ipo/order-management/place';

export default function InvestorProfilePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageBankDematAccounts = hasPermission(PERMISSIONS.INVESTORS_EDIT);
  const canSetPrimaryBank = hasPermission(PERMISSIONS.BANK_SET_PRIMARY);
  const canEditDematNumber = hasPermission(PERMISSIONS.DEMAT_NUMBER_EDIT);
  const canVerifyDematOcr = hasPermission(PERMISSIONS.DEMAT_OCR_VERIFY);
  const investorId = id ? String(id).trim() : '';
  const partnerUuidFromUrl = searchParams.get('partner_uuid')?.trim() || '';
  const tabFromUrl = searchParams.get('tab')?.trim() || '';
  const sectionFromUrl = searchParams.get('section')?.trim() || '';
  const allowedTabs = useMemo(
    () => getAllowedInvestorProfileTabs(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin]
  );
  const resolvedTabFromUrl = useMemo(
    () =>
      resolveInvestorProfileTabFromParams({
        tab: tabFromUrl,
        section: sectionFromUrl,
        hasPermission,
        isSuperAdmin,
      }),
    [tabFromUrl, sectionFromUrl, hasPermission, isSuperAdmin]
  );

  const [activeTab, setActiveTab] = useState(resolvedTabFromUrl);
  const [investor, setInvestor] = useState(null);
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});
  const [loadedTabs, setLoadedTabs] = useState({});
  const [bankForm] = Form.useForm();
  const [dematForm] = Form.useForm();
  const [dematEditForm] = Form.useForm();
  const [isBankFormOpen, setIsBankFormOpen] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankIfscLookupLoading, setBankIfscLookupLoading] = useState(false);
  const [bankIfscDetails, setBankIfscDetails] = useState(null);
  const [bankIfscError, setBankIfscError] = useState('');
  const [bankMismatchReview, setBankMismatchReview] = useState(null);
  const [bankMismatchLoading, setBankMismatchLoading] = useState(false);
  const [bankMismatchError, setBankMismatchError] = useState('');
  const [bankMismatchActionLoading, setBankMismatchActionLoading] = useState(false);
  const [bankManualUploadData, setBankManualUploadData] = useState(null);
  const [bankSubmitSuccessOpen, setBankSubmitSuccessOpen] = useState(false);
  const [bankSubmitSuccessMessage, setBankSubmitSuccessMessage] = useState('');
  const [settingPrimaryBankUuid, setSettingPrimaryBankUuid] = useState('');
  const [deletingBankUuid, setDeletingBankUuid] = useState('');
  const [isDematFormOpen, setIsDematFormOpen] = useState(false);
  const [savingDemat, setSavingDemat] = useState(false);
  const [dematCmrFile, setDematCmrFile] = useState(null);
  const [editingDemat, setEditingDemat] = useState(null);
  const [savingDematEdit, setSavingDematEdit] = useState(false);
  const [verifyingDematUuid, setVerifyingDematUuid] = useState('');
  const [basicProfileDrawerOpen, setBasicProfileDrawerOpen] = useState(false);
  const [savingBasicProfile, setSavingBasicProfile] = useState(false);
  const [basicProfileInitialLoading, setBasicProfileInitialLoading] = useState(false);
  const [basicProfileInitialValues, setBasicProfileInitialValues] = useState({
    mobile: '',
    email: '',
    dob: '',
  });

  const fetchers = useMemo(
    () => ({
      [TAB_KEYS.portfolio]: investorProfileService.getPortfolio,
      [TAB_KEYS.personal]: investorProfileService.getPersonalInfo,
      [TAB_KEYS.bankDetails]: investorProfileService.getBankDetails,
      [TAB_KEYS.dematDetails]: investorProfileService.getDematDetails,
      [TAB_KEYS.address]: investorProfileService.getAddress,
      [TAB_KEYS.professional]: investorProfileService.getProfessionalDetails,
      [TAB_KEYS.nominee]: investorProfileService.getNominee,
      [TAB_KEYS.preIpoTransactions]: investorProfileService.getPreIpoTransactionsAll,
    }),
    []
  );

  const ensureTabData = useCallback(
    async (tabKey, force = false) => {
      if (!canViewInvestorProfileTab(tabKey, hasPermission, isSuperAdmin)) return;
      if (!force && loadedTabs[tabKey]) return;
      const fetcher = fetchers[tabKey];
      if (!fetcher || !investorId) return;

      setTabLoading((prev) => ({ ...prev, [tabKey]: true }));
      setTabError((prev) => ({ ...prev, [tabKey]: '' }));
      try {
        const data = await fetcher(investorId);
        setTabData((prev) => ({ ...prev, [tabKey]: data }));
        setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
        if (data?.investor) setInvestor(data.investor);
      } catch (error) {
        setTabError((prev) => ({
          ...prev,
          [tabKey]: error?.response?.data?.message || 'Failed to load tab data.',
        }));
      } finally {
        setTabLoading((prev) => ({ ...prev, [tabKey]: false }));
      }
    },
    [fetchers, investorId, loadedTabs, hasPermission, isSuperAdmin]
  );

  const loadBankMismatchReview = useCallback(async () => {
    if (!investorId) return;
    setBankMismatchLoading(true);
    setBankMismatchError('');
    try {
      const data = await investorProfileService.getBankMismatchPendingReview(investorId);
      setBankMismatchReview(data);
    } catch (error) {
      setBankMismatchReview(null);
      setBankMismatchError(error?.response?.data?.message || 'Failed to load pending bank mismatch review.');
    } finally {
      setBankMismatchLoading(false);
    }
  }, [investorId]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.bankDetails || !loadedTabs[TAB_KEYS.bankDetails]) return;
    loadBankMismatchReview();
  }, [activeTab, loadedTabs, loadBankMismatchReview]);

  useEffect(() => {
    if (!investorId) return;
    setActiveTab(resolvedTabFromUrl);
    if (canViewInvestorProfileTab(resolvedTabFromUrl, hasPermission, isSuperAdmin)) {
      ensureTabData(resolvedTabFromUrl);
    }
  }, [investorId, resolvedTabFromUrl, ensureTabData, hasPermission, isSuperAdmin]);

  useEffect(() => {
    if (!investorId) return;
    const hasSectionOnly = sectionFromUrl && !tabFromUrl;
    const hasInvalidTab = tabFromUrl && tabFromUrl !== resolvedTabFromUrl;
    if (!hasSectionOnly && !hasInvalidTab) return;
    const params = new URLSearchParams();
    if (partnerUuidFromUrl) params.set('partner_uuid', partnerUuidFromUrl);
    if (resolvedTabFromUrl !== TAB_KEYS.portfolio) params.set('tab', resolvedTabFromUrl);
    setSearchParams(params, { replace: true });
  }, [
    investorId,
    tabFromUrl,
    sectionFromUrl,
    resolvedTabFromUrl,
    partnerUuidFromUrl,
    setSearchParams,
  ]);

  const handleTabChange = (nextKey) => {
    if (!canViewInvestorProfileTab(nextKey, hasPermission, isSuperAdmin)) return;
    setActiveTab(nextKey);
    ensureTabData(nextKey);
    const params = new URLSearchParams();
    if (partnerUuidFromUrl) params.set('partner_uuid', partnerUuidFromUrl);
    if (nextKey !== TAB_KEYS.portfolio) params.set('tab', nextKey);
    setSearchParams(params, { replace: true });
  };

  const patchPreIpoTransactionRow = useCallback((orderUuid, patch) => {
    if (!orderUuid || !patch) return;
    setTabData((prev) => {
      const key = TAB_KEYS.preIpoTransactions;
      const tab = prev[key];
      const rows = tab?.pre_ipo_transactions_all;
      if (!Array.isArray(rows)) return prev;
      return {
        ...prev,
        [key]: {
          ...tab,
          pre_ipo_transactions_all: rows.map((row) =>
            row.uuid === orderUuid ? { ...row, ...patch } : row
          ),
        },
      };
    });
  }, []);

  const handleSellClick = useCallback(() => {
    if (!investorId) return;
    const params = new URLSearchParams({ investor_id: investorId });
    const partnerUuid =
      partnerUuidFromUrl || investor?.partner_uuid;
    if (partnerUuid != null && String(partnerUuid).trim() !== '') {
      params.set('partner_uuid', String(partnerUuid).trim());
    }
    navigate(`${PLACE_SINGLE_SHARE_ORDER_PATH}?${params.toString()}`);
  }, [investorId, partnerUuidFromUrl, investor, navigate]);

  const openBasicProfileDrawer = useCallback(async () => {
    if (!investorId) return;
    setBasicProfileDrawerOpen(true);
    setBasicProfileInitialLoading(true);

    let dob = tabData[TAB_KEYS.personal]?.personal_info?.dob || '';

    if (!loadedTabs[TAB_KEYS.personal]) {
      try {
        const data = await investorProfileService.getPersonalInfo(investorId);
        dob = data?.personal_info?.dob || '';
      } catch {
        // leave dob blank
      }
    }

    setBasicProfileInitialValues({
      mobile: investor?.mobile || '',
      email: investor?.email || '',
      dob,
    });
    setBasicProfileInitialLoading(false);
  }, [investorId, investor, tabData, loadedTabs]);

  const refreshAfterBasicProfileEdit = useCallback(async () => {
    const tabsToRefresh = new Set([activeTab]);

    if (canViewInvestorProfileTab(TAB_KEYS.portfolio, hasPermission, isSuperAdmin)) {
      tabsToRefresh.add(TAB_KEYS.portfolio);
    }

    if (canViewInvestorProfileTab(TAB_KEYS.personal, hasPermission, isSuperAdmin)) {
      tabsToRefresh.add(TAB_KEYS.personal);
    }

    await Promise.all([...tabsToRefresh].map((tabKey) => ensureTabData(tabKey, true)));
  }, [activeTab, ensureTabData, hasPermission, isSuperAdmin]);

  const handleBasicProfileSubmit = async (payload) => {
    if (!investorId) return;
    setSavingBasicProfile(true);
    try {
      const response = await investorProfileService.updateBasicProfile(investorId, payload);
      await refreshAfterBasicProfileEdit();
      toast.success(response?.message || 'Basic profile updated successfully.');
      setBasicProfileDrawerOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update basic profile.');
      throw error;
    } finally {
      setSavingBasicProfile(false);
    }
  };

  const handleAddBankAccount = async (values) => {
    if (!investorId || !canManageBankDematAccounts) return;
    const payload = {
      account_number: String(values.account_number || '').trim(),
      ifsc_code: String(values.ifsc_code || '').trim().toUpperCase(),
    };

    setSavingBank(true);
    try {
      const response = await investorProfileService.addBankAccount(investorId, payload);
      if (response?.success === false) {
        const attempted = getProfileAttemptedBankFields(bankForm, bankIfscDetails);
        const manualUpload = buildBankManualUploadState(response, attempted);
        applyProfileBankManualUpload(manualUpload, {
          bankForm,
          setBankIfscDetails,
          setManualUploadData: setBankManualUploadData,
          setBankIfscError,
        });
        return;
      }
      toast.success(response?.message || 'Bank account added successfully.');
      resetProfileBankAddForm({
        bankForm,
        setBankIfscDetails,
        setBankIfscError,
        setManualUploadData: setBankManualUploadData,
        setIsBankFormOpen,
      });
      await ensureTabData(TAB_KEYS.bankDetails, true);
      await loadBankMismatchReview();
    } catch (error) {
      const attempted = getProfileAttemptedBankFields(bankForm, bankIfscDetails);
      const manualUpload = buildBankManualUploadState(error, attempted);
      applyProfileBankManualUpload(manualUpload, {
        bankForm,
        setBankIfscDetails,
        setManualUploadData: setBankManualUploadData,
        setBankIfscError,
      });
    } finally {
      setSavingBank(false);
    }
  };

  const handleManualUploadSubmit = async ({
    accountNumber,
    ifscCode,
    bankName,
    branchName,
    chequeFile,
    otherDocFile,
  }) => {
    if (!investorId || !canManageBankDematAccounts) return;
    setSavingBank(true);
    try {
      const fd = new FormData();
      fd.append('account_number', accountNumber);
      fd.append('ifsc_code', ifscCode);
      fd.append('bank_name', bankName);
      fd.append('branch_name', branchName || '');
      fd.append('cheque', chequeFile);
      if (otherDocFile) fd.append('supporting_document', otherDocFile);
      const response = await investorProfileService.uploadBankMismatchCheque(investorId, fd);
      resetProfileBankAddForm({
        bankForm,
        setBankIfscDetails,
        setBankIfscError,
        setManualUploadData: setBankManualUploadData,
        setIsBankFormOpen,
      });
      setBankSubmitSuccessMessage(response?.message || '');
      setBankSubmitSuccessOpen(true);
      await ensureTabData(TAB_KEYS.bankDetails, true);
      await loadBankMismatchReview();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to upload documents.');
    } finally {
      setSavingBank(false);
    }
  };

  const handleManualUploadCancel = () => {
    setBankManualUploadData(null);
    bankForm.resetFields();
    setBankIfscDetails(null);
    setBankIfscError('');
  };

  const handleBankIfscChange = async (value) => {
    const nextIfsc = String(value || '').trim().toUpperCase();
    setBankIfscDetails(null);
    setBankIfscError('');
    bankForm.setFieldValue('ifsc_code', nextIfsc);

    if (!IFSC_REGEX.test(nextIfsc)) {
      setBankIfscLookupLoading(false);
      return;
    }

    setBankIfscLookupLoading(true);
    try {
      const response = await ifaService.lookupIFSC(nextIfsc);
      if (bankForm.getFieldValue('ifsc_code') !== nextIfsc) return;
      const details = response?.data || {};
      setBankIfscDetails({
        bank_name: details.bank_name || '',
        branch_name: details.branch_name || '',
        address: details.address || '',
      });
    } catch (error) {
      if (bankForm.getFieldValue('ifsc_code') !== nextIfsc) return;
      setBankIfscDetails(null);
      setBankIfscError(error?.response?.data?.message || 'Could not fetch bank details for this IFSC.');
    } finally {
      if (bankForm.getFieldValue('ifsc_code') === nextIfsc) {
        setBankIfscLookupLoading(false);
      }
    }
  };

  const handleSetPrimaryBank = async (bankUuid) => {
    if (!investorId || !canSetPrimaryBank || !bankUuid) return;
    setSettingPrimaryBankUuid(bankUuid);
    try {
      const response = await investorProfileService.setPrimaryBankAccount(investorId, bankUuid);
      toast.success(response?.message || 'Primary bank account updated successfully.');
      await ensureTabData(TAB_KEYS.bankDetails, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to set primary bank account.');
    } finally {
      setSettingPrimaryBankUuid('');
    }
  };

  const handleDeleteBankAccount = async (bankUuid) => {
    if (!investorId || !canManageBankDematAccounts || !bankUuid) return;
    setDeletingBankUuid(bankUuid);
    try {
      const response = await investorProfileService.deleteBankAccount(investorId, bankUuid);
      toast.success(response?.message || 'Bank account removed successfully.');
      await ensureTabData(TAB_KEYS.bankDetails, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove bank account.');
    } finally {
      setDeletingBankUuid('');
    }
  };

  const handleBankMismatchVerify = async (status) => {
    if (!investorId || !canManageBankDematAccounts) return;
    const documentId = bankMismatchReview?.pending_review?.cheque_document?.document_id;
    if (!documentId) return;

    setBankMismatchActionLoading(true);
    try {
      const response = await investorProfileService.verifyBankMismatchDocument(
        investorId,
        documentId,
        status
      );
      toast.success(
        response?.message
          || (status === 'VERIFIED' ? 'Document verified successfully.' : 'Document rejected successfully.')
      );
      await ensureTabData(TAB_KEYS.bankDetails, true);
      await loadBankMismatchReview();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update bank mismatch review.');
    } finally {
      setBankMismatchActionLoading(false);
    }
  };

  const handleDematCmrFileChange = (file) => {
    if (!file) return;
    if (file.size > DEMAT_CMR_MAX_SIZE) {
      toast.error('CMR document size must be under 2 MB.');
      return;
    }
    if (!DEMAT_CMR_ALLOWED_TYPES.includes(file.type) && !DEMAT_CMR_ALLOWED_EXTENSIONS.test(file.name || '')) {
      toast.error('Only PDF, JPG, JPEG, PNG, or WebP files are allowed.');
      return;
    }
    setDematCmrFile(file);
  };

  const handleAddDematAccount = async (values) => {
    if (!investorId || !canManageBankDematAccounts) return;
    if (!dematCmrFile) {
      toast.error('Please upload a CMR document.');
      return;
    }
    if (dematCmrFile.size > DEMAT_CMR_MAX_SIZE) {
      toast.error('CMR document size must be under 2 MB.');
      return;
    }
    if (
      !DEMAT_CMR_ALLOWED_TYPES.includes(dematCmrFile.type)
      && !DEMAT_CMR_ALLOWED_EXTENSIONS.test(dematCmrFile.name || '')
    ) {
      toast.error('Only PDF, JPG, JPEG, PNG, or WebP files are allowed.');
      return;
    }

    const dematCheck = validateDematNumber(values.demat_number);
    if (!dematCheck.valid) {
      toast.error(dematCheck.message || 'Enter a valid demat number.');
      return;
    }

    const dematNumber = String(values.demat_number || '').trim().toUpperCase();
    const formData = new FormData();
    formData.append('demat_number', dematNumber);
    formData.append('file', dematCmrFile, dematCmrFile.name || 'cmr-document');

    setSavingDemat(true);
    try {
      const response = await investorProfileService.uploadDematCmr(investorId, formData);
      toast.success(response?.message || 'Demat account added successfully.');
      dematForm.resetFields();
      setDematCmrFile(null);
      setIsDematFormOpen(false);
      await ensureTabData(TAB_KEYS.dematDetails, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add demat account.');
    } finally {
      setSavingDemat(false);
    }
  };

  const handleOpenDematEdit = (demat) => {
    const dematUuid = demat?.uuid;
    if (!dematUuid) {
      toast.error('Demat account identifier is missing.');
      return;
    }
    const currentNumber = String(demat.demat_number || demat.demat_account_no || '').trim();
    setEditingDemat({ uuid: dematUuid, demat_number: currentNumber });
    dematEditForm.setFieldsValue({ demat_number: currentNumber });
  };

  const handleCloseDematEdit = () => {
    setEditingDemat(null);
    dematEditForm.resetFields();
  };

  const handleEditDematNumber = async (values) => {
    if (!investorId || !canEditDematNumber || !editingDemat?.uuid) return;

    const dematCheck = validateDematNumber(values.demat_number);
    if (!dematCheck.valid) {
      toast.error(dematCheck.message || 'Enter a valid demat number.');
      return;
    }

    const dematNumber = String(values.demat_number || '').trim().toUpperCase();
    setSavingDematEdit(true);
    try {
      const response = await investorProfileService.editDematAccount(investorId, editingDemat.uuid, {
        demat_number: dematNumber,
      });
      toast.success(response?.message || 'Demat number updated successfully.');
      handleCloseDematEdit();
      await ensureTabData(TAB_KEYS.dematDetails, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update demat number.');
    } finally {
      setSavingDematEdit(false);
    }
  };

  const handleVerifyDemat = async (demat) => {
    if (!investorId || !canVerifyDematOcr || !demat?.uuid) return;

    setVerifyingDematUuid(demat.uuid);
    try {
      const response = await investorProfileService.verifyDematAccount(investorId, demat.uuid);
      toast.success(response?.message || 'Demat account marked as verified.');
      await ensureTabData(TAB_KEYS.dematDetails, true);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        toast.error('You do not have permission to verify demat accounts.');
      } else if (status === 404) {
        toast.error('Demat account not found.');
      } else {
        toast.error(error?.response?.data?.message || 'Failed to verify demat account.');
      }
    } finally {
      setVerifyingDematUuid('');
    }
  };

  const renderTabBody = (tabKey, content) => {
    if (tabLoading[tabKey]) {
      return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      );
    }
    if (tabError[tabKey]) {
      return (
        <Alert
          type="error"
          showIcon
          message={tabError[tabKey]}
          action={
            <Button size="small" onClick={() => ensureTabData(tabKey, true)}>
              Retry
            </Button>
          }
        />
      );
    }
    return content;
  };

  const portfolioData = tabData[TAB_KEYS.portfolio] || {};
  const personalData = tabData[TAB_KEYS.personal] || {};
  const bankDetailsData = tabData[TAB_KEYS.bankDetails] || {};
  const activeBankAccounts = (bankDetailsData.bank_details || []).filter(
    (bank) => bank.is_active !== false
  );
  const dematDetailsData = tabData[TAB_KEYS.dematDetails] || {};
  const addressData = tabData[TAB_KEYS.address] || {};
  const professionalData = tabData[TAB_KEYS.professional] || {};
  const nomineeData = tabData[TAB_KEYS.nominee] || {};
  const preIpoTransactionsData = tabData[TAB_KEYS.preIpoTransactions] || {};

  const transactions = portfolioData.pre_ipo_transactions || [];
  const allPreIpoTransactions = preIpoTransactionsData.pre_ipo_transactions_all || [];
  const portfolio = portfolioData.portfolio || {};

  const transactionColumns = [
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      render: (value) => <span className="font-mono text-sm">{safeValue(value)}</span>,
    },
    {
      title: 'Share Name',
      dataIndex: 'share_name',
      key: 'share_name',
    },
    {
      title: 'Qty',
      dataIndex: 'requested_qty',
      key: 'requested_qty',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (value) => <span className="font-semibold">{formatCurrency(value)}</span>,
    },
    {
      title: 'Date',
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      render: formatDate,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, row) => (
        <Tag color={getInvestorPreIpoStatusColor(row)}>
          {getInvestorPreIpoStatusLabel(row)}
        </Tag>
      ),
    },
    {
      title: 'Deal Slip',
      dataIndex: 'deal_slip',
      key: 'deal_slip',
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
            View
          </a>
        ) : (
          '—'
        ),
    },
  ];

  const resolveLookup = (items, valueKey, idOrValue) => {
    if (!idOrValue) return '—';
    const match = (items || []).find((entry) => String(entry.id) === String(idOrValue));
    return match?.[valueKey] || idOrValue;
  };

  const tabItems = [
    {
      key: TAB_KEYS.portfolio,
      label: 'Portfolio',
      children: renderTabBody(
        TAB_KEYS.portfolio,
        <div className="space-y-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h3 className="text-lg font-semibold text-gray-900">Investor Portfolio Overview</h3>
            <PermissionGate permission={PERMISSIONS.INVESTORS_PLACE_SELL_ORDER}>
              <Button type="primary" className="font-semibold" onClick={handleSellClick} disabled={!investorId}>
                Click here to sell
              </Button>
            </PermissionGate>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card bordered={false} className="rounded-2xl shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm">Total Investment</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(portfolio.total_investment)}</p>
            </Card>
            <Card bordered={false} className="rounded-2xl shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm">Total Shares</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{safeValue(portfolio.total_shares)}</p>
            </Card>
          </div>

          <Card
            bordered={false}
            className="rounded-2xl shadow-sm border border-gray-100"
            title={<span className="font-semibold">Pre-IPO Transactions</span>}
          >
            <Table
              columns={transactionColumns}
              dataSource={transactions}
              rowKey={(record) => record.uuid || record.id}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{ emptyText: <Empty description="No transactions found." /> }}
              scroll={{ x: 900 }}
            />
          </Card>
        </div>
      ),
    },
    {
      key: TAB_KEYS.personal,
      label: 'Personal Info',
      children: renderTabBody(
        TAB_KEYS.personal,
        <Card bordered={false} className="rounded-2xl shadow-sm border border-gray-100">
          <InfoGrid
            items={[
              { label: 'Date of Birth', value: personalData.personal_info?.dob ? dayjs(personalData.personal_info.dob).format('DD MMM YYYY') : '—' },
              { label: 'Gender', value: personalData.personal_info?.gender },
              { label: 'Entity Type', value: personalData.personal_info?.entity_type },
            ]}
          />
        </Card>
      ),
    },
    {
      key: TAB_KEYS.bankDetails,
      label: 'Bank Details',
      children: renderTabBody(
        TAB_KEYS.bankDetails,
        <Card
          bordered={false}
          className="rounded-2xl shadow-sm border border-gray-100"
          title="Bank Accounts"
          extra={
            <PermissionGate permission={PERMISSIONS.INVESTORS_EDIT}>
              <Button
                type={isBankFormOpen ? 'default' : 'primary'}
                icon={isBankFormOpen ? <FiX /> : <FiPlus />}
                onClick={() => {
                  if (isBankFormOpen) {
                    resetProfileBankAddForm({
                      bankForm,
                      setBankIfscDetails,
                      setBankIfscError,
                      setManualUploadData: setBankManualUploadData,
                      setIsBankFormOpen,
                    });
                  } else {
                    setIsBankFormOpen(true);
                  }
                }}
              >
                {isBankFormOpen ? 'Cancel' : 'Add Bank'}
              </Button>
            </PermissionGate>
          }
        >
          <div className="flex flex-col gap-6">
            <BankMismatchReviewPanel
              reviewData={bankMismatchReview}
              loading={bankMismatchLoading}
              error={bankMismatchError}
              actionLoading={bankMismatchActionLoading}
              canAct={canManageBankDematAccounts}
              onVerify={handleBankMismatchVerify}
            />

            {canManageBankDematAccounts && isBankFormOpen ? (
              <div className="rounded-xl border border-green-100 bg-green-50/30 p-4">
                {bankManualUploadData ? (
                  <BankManualUploadForm
                    manualUploadData={bankManualUploadData}
                    loading={savingBank}
                    onSubmit={handleManualUploadSubmit}
                    onCancel={handleManualUploadCancel}
                  />
                ) : (
                <Form form={bankForm} layout="vertical" onFinish={handleAddBankAccount}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    <Form.Item
                      label="Account Number"
                      name="account_number"
                      rules={[{ required: true, message: 'Enter account number.' }]}
                    >
                      <Input placeholder="Enter bank account number" disabled={savingBank} />
                    </Form.Item>
                    <Form.Item
                      label="IFSC Code"
                      name="ifsc_code"
                      normalize={(value) => String(value || '').toUpperCase()}
                      rules={[
                        { required: true, message: 'Enter IFSC code.' },
                        { pattern: IFSC_REGEX, message: 'Enter a valid IFSC code.' },
                      ]}
                    >
                      <Input
                        placeholder="HDFC0000522"
                        maxLength={11}
                        disabled={savingBank}
                        onChange={(event) => handleBankIfscChange(event.target.value)}
                      />
                    </Form.Item>
                  </div>

                  {bankIfscLookupLoading ? (
                    <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                      Fetching bank details...
                    </div>
                  ) : null}
                  {bankIfscDetails ? (
                    <div className="mb-4 rounded-lg border border-green-100 bg-white px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">IFSC Bank Details</p>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <p><span className="text-gray-500">Bank:</span> {safeValue(bankIfscDetails.bank_name)}</p>
                        <p><span className="text-gray-500">Branch:</span> {safeValue(bankIfscDetails.branch_name)}</p>
                        {bankIfscDetails.address ? (
                          <p className="md:col-span-2">
                            <span className="text-gray-500">Address:</span> {bankIfscDetails.address}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {bankIfscError ? (
                    <Alert type="error" showIcon message={bankIfscError} className="mb-4" />
                  ) : null}

                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => {
                        resetProfileBankAddForm({
                          bankForm,
                          setBankIfscDetails,
                          setBankIfscError,
                          setManualUploadData: setBankManualUploadData,
                          setIsBankFormOpen,
                        });
                      }}
                      disabled={savingBank}
                    >
                      Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={savingBank}>
                      Add Bank Account
                    </Button>
                  </div>
                </Form>
                )}
              </div>
            ) : null}

            {activeBankAccounts.length === 0 ? (
              <Empty description="No bank details available." />
            ) : (
              <div className="space-y-3">
                {activeBankAccounts.map((bank, idx) => (
                  <div key={bank.uuid || bank.id} className="rounded-xl border border-gray-200 p-4 bg-gray-50/40">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">
                          Bank Account {idx + 1}
                        </p>
                        {bank.is_primary ? (
                          <Tag color="green" className="m-0 font-medium">
                            Primary
                          </Tag>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {canSetPrimaryBank && !bank.is_primary && bank.is_verified && bank.uuid ? (
                          <Popconfirm
                            title="Set as primary bank account?"
                            description="This will demote the current primary account for this PAN."
                            okText="Set Primary"
                            onConfirm={() => handleSetPrimaryBank(bank.uuid)}
                          >
                            <Button
                              size="small"
                              loading={settingPrimaryBankUuid === bank.uuid}
                              disabled={Boolean(
                                (settingPrimaryBankUuid && settingPrimaryBankUuid !== bank.uuid) ||
                                  deletingBankUuid
                              )}
                            >
                              Set as Primary
                            </Button>
                          </Popconfirm>
                        ) : null}
                        {canManageBankDematAccounts && bank.uuid ? (
                          <Popconfirm
                            title="Remove this bank account?"
                            description="This will deactivate the bank account."
                            okText="Remove"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDeleteBankAccount(bank.uuid)}
                          >
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              loading={deletingBankUuid === bank.uuid}
                              disabled={Boolean(
                                (deletingBankUuid && deletingBankUuid !== bank.uuid) ||
                                  settingPrimaryBankUuid
                              )}
                            >
                              Delete
                            </Button>
                          </Popconfirm>
                        ) : null}
                      </div>
                    </div>
                    <InfoGrid
                      items={[
                        { label: 'Bank Name', value: bank.bank_name },
                        { label: 'Account Number', value: bank.account_number },
                        { label: 'IFSC Code', value: bank.ifsc_code },
                        { label: 'Beneficiary Name', value: bank.beneficiary_name },
                        { label: 'Verified', value: bank.is_verified ? 'Yes' : 'No' },
                      ]}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      ),
    },
    {
      key: TAB_KEYS.dematDetails,
      label: 'Demat Details',
      children: renderTabBody(
        TAB_KEYS.dematDetails,
        <Card
          bordered={false}
          className="rounded-2xl shadow-sm border border-gray-100"
          title="Demat Accounts"
          extra={
            <PermissionGate permission={PERMISSIONS.INVESTORS_EDIT}>
              <Button
                type={isDematFormOpen ? 'default' : 'primary'}
                icon={isDematFormOpen ? <FiX /> : <FiPlus />}
                onClick={() => {
                  if (isDematFormOpen) {
                    dematForm.resetFields();
                    setDematCmrFile(null);
                  }
                  setIsDematFormOpen((open) => !open);
                }}
              >
                {isDematFormOpen ? 'Cancel' : 'Add Demat'}
              </Button>
            </PermissionGate>
          }
        >
          <div className="space-y-4">
            {canManageBankDematAccounts && isDematFormOpen ? (
              <div className="rounded-xl border border-green-100 bg-green-50/30 p-4">
                <Form form={dematForm} layout="vertical" onFinish={handleAddDematAccount}>
                  <Form.Item
                    label="Demat Number"
                    name="demat_number"
                    normalize={formatDematInput}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[
                      { required: true, message: 'Enter demat number.' },
                      {
                        validator: async (_, value) => {
                          const inlineError = getDematInputError(value);
                          if (inlineError) throw new Error(inlineError);
                          const result = validateDematNumber(value);
                          if (!result.valid) throw new Error(result.message);
                        },
                      },
                    ]}
                    extra={<span className="text-xs text-gray-500">{DEMAT_FORMAT_HINT}</span>}
                  >
                    <Input
                      placeholder="IN12345678901234 or 16-digit CDSL"
                      maxLength={DEMAT_MAX_LENGTH}
                      disabled={savingDemat}
                    />
                  </Form.Item>

                  <div className="mb-4 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800">CMR Document</p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {dematCmrFile ? dematCmrFile.name : 'Upload PDF, JPG, PNG, or WebP under 2 MB.'}
                        </p>
                      </div>
                      <label className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 cursor-pointer hover:bg-green-100">
                        <FiUpload />
                        Choose File
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          disabled={savingDemat}
                          onChange={(event) => {
                            const selectedFile = event.target.files?.[0];
                            handleDematCmrFileChange(selectedFile);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => {
                        dematForm.resetFields();
                        setDematCmrFile(null);
                        setIsDematFormOpen(false);
                      }}
                      disabled={savingDemat}
                    >
                      Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={savingDemat}>
                      Add Demat Account
                    </Button>
                  </div>
                </Form>
              </div>
            ) : null}

            {(dematDetailsData.demat_details || []).length === 0 ? (
              <Empty description="No demat details available." />
            ) : (
              <div className="space-y-3">
                {(dematDetailsData.demat_details || []).map((demat, idx) => {
                  const ocrUi = getDematOcrUiState(demat);
                  const showVerifyButton = ocrUi.showVerify && canVerifyDematOcr && demat.uuid;

                  return (
                  <div key={demat.uuid || demat.id} className="rounded-xl border border-gray-200 p-4 bg-gray-50/40">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <p className="text-sm font-semibold text-gray-800">
                        Demat Account {idx + 1}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {canEditDematNumber && demat.uuid ? (
                          <PermissionGate permission={PERMISSIONS.DEMAT_NUMBER_EDIT}>
                            <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined />}
                              className="px-1"
                              onClick={() => handleOpenDematEdit(demat)}
                            >
                              Edit
                            </Button>
                          </PermissionGate>
                        ) : null}
                        {demat.is_primary ? (
                          <Tag color="green" className="m-0 font-medium">
                            Primary
                          </Tag>
                        ) : null}
                        {ocrUi.badge ? (
                          <span className="inline-flex items-center gap-1">
                            <Tag color={ocrUi.badge.color} className="m-0 font-medium">
                              {ocrUi.badge.label}
                            </Tag>
                            {ocrUi.messageLines.length > 0 ? (
                              <Tooltip
                                title={(
                                  <div className="space-y-1">
                                    {ocrUi.messageLines.map((line, lineIdx) => (
                                      <p key={`${ocrUi.kind}-${lineIdx}`} className="m-0 text-xs">
                                        {line}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              >
                                <InfoCircleOutlined className="cursor-help text-gray-400 hover:text-blue-500" />
                              </Tooltip>
                            ) : null}
                          </span>
                        ) : null}
                        {showVerifyButton ? (
                          <PermissionGate permission={PERMISSIONS.DEMAT_OCR_VERIFY}>
                            <Popconfirm
                              title="Verify demat account?"
                              description="Confirm you have matched the saved demat number with the CMR/CML document."
                              onConfirm={() => handleVerifyDemat(demat)}
                              okText="Verify"
                              cancelText="Cancel"
                            >
                              <Button
                                type="primary"
                                size="small"
                                loading={verifyingDematUuid === demat.uuid}
                                disabled={Boolean(verifyingDematUuid)}
                              >
                                Verify
                              </Button>
                            </Popconfirm>
                          </PermissionGate>
                        ) : null}
                      </div>
                    </div>
                    <InfoGrid
                      items={[
                        { label: 'DP Name', value: demat.dp_name },
                        { label: 'Demat Number', value: demat.demat_number },
                        {
                          label: 'CMR/CML Document',
                          value: demat.cmr_document_url ? (
                            <a
                              href={normalizeDocumentUrl(demat.cmr_document_url)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              View Document
                            </a>
                          ) : (
                            '—'
                          ),
                        },
                      ]}
                    />
                    {ocrUi.details ? (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
                          Match Score: <b className="font-mono">{ocrUi.details.matchScore}</b>
                        </span>
                        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700">
                          Saved: <b className="font-mono">{ocrUi.details.savedDemat}</b>
                        </span>
                        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700">
                          OCR: <b className="font-mono">{ocrUi.details.ocrExtracted}</b>
                        </span>
                      </div>
                    ) : null}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      ),
    },
    {
      key: TAB_KEYS.address,
      label: 'Address',
      children: renderTabBody(
        TAB_KEYS.address,
        <Card bordered={false} className="rounded-t-2xl rounded-b-none shadow-sm border border-gray-100">
          {(addressData.address || []).length === 0 ? (
            <Empty description="No address details available." />
          ) : (
            <div className="space-y-3">
              {(addressData.address || []).map((addr) => (
                <div key={addr.uuid || addr.id} className="rounded-xl border border-gray-200 p-4 bg-gray-50/40">
                  <InfoGrid
                    items={[
                      { label: 'Address Type', value: addr.address_type },
                      { label: 'Address Line 1', value: addr.address_line1 },
                      { label: 'City', value: addr.city },
                      { label: 'State', value: addr.state },
                      { label: 'Pincode', value: addr.pincode },
                      { label: 'Country', value: addr.country },
                    ]}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      ),
    },
    {
      key: TAB_KEYS.professional,
      label: 'Professional',
      children: renderTabBody(
        TAB_KEYS.professional,
        <Card bordered={false} className="rounded-2xl shadow-sm border border-gray-100">
          <InfoGrid
            items={[
              {
                label: 'Occupation',
                value: resolveLookup(
                  professionalData.occuption,
                  'occupation_name',
                  professionalData.professional_details?.occupation
                ),
              },
              {
                label: 'Annual Income',
                value: resolveLookup(
                  professionalData.annual_income,
                  'annual_income',
                  professionalData.professional_details?.annual_income
                ),
              },
              {
                label: 'Income Source',
                value: resolveLookup(
                  professionalData.income_source,
                  'income_source',
                  professionalData.professional_details?.income_source
                ),
              },
            ]}
          />
        </Card>
      ),
    },
    {
      key: TAB_KEYS.nominee,
      label: 'Nominees',
      children: renderTabBody(
        TAB_KEYS.nominee,
        <Card bordered={false} className="rounded-2xl shadow-sm border border-gray-100">
          {(nomineeData.nominee || []).length === 0 ? (
            <Empty description="No nominee details available." />
          ) : (
            <div className="space-y-3">
              {(nomineeData.nominee || []).map((nominee) => (
                <div key={nominee.id} className="rounded-xl border border-gray-200 p-4 bg-gray-50/40">
                  <InfoGrid
                    items={[
                      { label: 'Nominee Name', value: nominee.nominee_name },
                      { label: 'Relationship', value: nominee.relationship },
                      { label: 'Share Percentage', value: nominee.share_percentage ? `${nominee.share_percentage}%` : '—' },
                      { label: 'Minor', value: nominee.is_minor ? 'Yes' : 'No' },
                      { label: 'Guardian Name', value: nominee.guardian_name },
                    ]}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      ),
    },
    {
      key: TAB_KEYS.preIpoTransactions,
      label: 'Pre IPO Transactions',
      children: renderTabBody(
        TAB_KEYS.preIpoTransactions,
        <PreIpoTransactionsTab
          transactions={allPreIpoTransactions}
          investorName={investor?.name}
          onRefresh={() => ensureTabData(TAB_KEYS.preIpoTransactions, true)}
          onDealSheetUpdated={patchPreIpoTransactionRow}
        />
      ),
    },
  ];

  const visibleTabItems = tabItems.filter((tab) => allowedTabs.includes(tab.key));
  const isHeaderLoading = Boolean(tabLoading[activeTab]) && !investor;

  return (
    <DashboardPageWrapper>
      <div className="space-y-0">
        <div className="rounded-t-2xl rounded-b-none shadow-sm border border-gray-100 overflow-hidden bg-white">
          {!investorId && (
            <Alert
              type="warning"
              showIcon
              className="m-4"
              message="Invalid investor identifier in URL. Please open profile from investor list."
            />
          )}
          {isHeaderLoading ? (
            <div className="p-5 sm:p-6">
              <Skeleton active paragraph={{ rows: 3 }} />
            </div>
          ) : (
            <div className="bg-linear-to-r from-emerald-50/60 via-white to-white p-5 sm:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => navigate('/investors/list')}
                      className="mb-2 inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors"
                    >
                      <FiArrowLeft size={14} />
                      Back to Investor List
                    </button>
                    <div className="flex items-center gap-2 min-w-0">
                      <h2 className="text-2xl font-bold text-gray-900 leading-tight truncate">{safeValue(investor?.name)}</h2>
                      <PermissionGate permission={PERMISSIONS.INVESTOR_BASIC_PROFILE_EDIT}>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={openBasicProfileDrawer}
                          aria-label="Edit basic profile"
                          className="shrink-0 text-gray-500 hover:text-gray-800"
                        >
                          Edit
                        </Button>
                      </PermissionGate>
                    </div>
                  </div>
                </div>

                <div className="text-left lg:text-right">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Created On</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1">
                    {investor?.created_at ? dayjs(investor.created_at).format('DD MMM YYYY') : '—'}
                  </p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">PAN</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{safeValue(investor?.pan)}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">Mobile</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{safeValue(investor?.mobile)}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 sm:col-span-2 lg:col-span-1">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">Email</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{safeValue(investor?.email)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-t-none rounded-b-2xl shadow-sm border border-gray-100 -mt-px bg-white overflow-hidden px-5 sm:px-6 pb-5 sm:pb-6">
          {visibleTabItems.length > 0 ? (
            <Tabs activeKey={activeTab} onChange={handleTabChange} items={visibleTabItems} />
          ) : (
            <Alert
              type="warning"
              showIcon
              className="my-4"
              message="You do not have permission to view any investor profile sections."
            />
          )}
        </div>
      </div>

      <Modal
        title="Edit Demat Number"
        open={Boolean(editingDemat)}
        onCancel={handleCloseDematEdit}
        footer={null}
        destroyOnClose
      >
        <Form form={dematEditForm} layout="vertical" onFinish={handleEditDematNumber}>
          <Form.Item
            label="Demat Number"
            name="demat_number"
            normalize={formatDematInput}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              { required: true, message: 'Enter demat number.' },
              {
                validator: async (_, value) => {
                  const inlineError = getDematInputError(value);
                  if (inlineError) throw new Error(inlineError);
                  const result = validateDematNumber(value);
                  if (!result.valid) throw new Error(result.message);
                },
              },
            ]}
            extra={<span className="text-xs text-gray-500">{DEMAT_FORMAT_HINT}</span>}
          >
            <Input
              placeholder="IN12345678901234 or 16-digit CDSL"
              maxLength={DEMAT_MAX_LENGTH}
              disabled={savingDematEdit}
            />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={handleCloseDematEdit} disabled={savingDematEdit}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={savingDematEdit}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>

      <BasicProfileEditDrawer
        open={basicProfileDrawerOpen}
        title={`Edit Basic Profile${investor?.name ? ` — ${investor.name}` : ''}`}
        initialValues={basicProfileInitialValues}
        loadingInitial={basicProfileInitialLoading}
        submitting={savingBasicProfile}
        onClose={() => setBasicProfileDrawerOpen(false)}
        onSubmit={handleBasicProfileSubmit}
      />

      <BankRequestSubmittedModal
        open={bankSubmitSuccessOpen}
        message={bankSubmitSuccessMessage}
        onClose={() => {
          setBankSubmitSuccessOpen(false);
          setBankSubmitSuccessMessage('');
        }}
      />
    </DashboardPageWrapper>
  );
}
